import { parseImportUrls } from '@platewell/core';

export interface UrlImportFieldProps {
  id: string;
  testId: string;
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  busy: boolean;
  /** Label for a single URL; a batch counts itself. */
  buttonLabel: string;
  buttonClassName?: string;
  /** Focus the box as soon as it appears. */
  autoFocus?: boolean;
}

const MIN_ROWS = 2;
const MAX_ROWS = 12;
const PLACEHOLDER = 'https://example.com/recipe\nhttps://example.com/another-recipe';

/**
 * The recipe URL box: a two-line paste area that grows a line per URL.
 *
 * Starting at two lines shows up front that a list is welcome, and the box keeps its size
 * when clicked. Pressing Enter or pasting several links grows it, and the whole batch is
 * imported together.
 */
export function UrlImportField({
  id,
  testId,
  value,
  onChange,
  onSubmit,
  busy,
  buttonLabel,
  buttonClassName = 'btn',
  autoFocus = false,
}: UrlImportFieldProps) {
  const lineCount = value.split('\n').length;
  const { urls } = parseImportUrls(value);

  return (
    // `display: contents` keeps the textarea and button laid out by the parent, as they were
    // before the form existed; some parents space their children with a flex gap.
    <form
      style={{ display: 'contents' }}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <textarea
        id={id}
        className="textarea"
        rows={Math.min(MAX_ROWS, Math.max(MIN_ROWS, lineCount))}
        placeholder={PLACEHOLDER}
        value={value}
        data-testid={testId}
        disabled={busy}
        autoFocus={autoFocus}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          // A textarea never submits on Enter by itself. Enter starts a new line for another
          // URL, so ⌘/Ctrl+Enter imports instead.
          if (event.key !== 'Enter' || (!event.metaKey && !event.ctrlKey)) return;
          event.preventDefault();
          event.currentTarget.form?.requestSubmit();
        }}
        style={{ minHeight: 0 }}
      />
      <div className="row" style={{ gap: 8, marginTop: 8, alignItems: 'center' }}>
        <button className={buttonClassName} type="submit" disabled={busy || urls.length === 0}>
          {busy ? 'Importing…' : urls.length > 1 ? `Import ${urls.length} URLs` : buttonLabel}
        </button>
        <span className="hint">One URL per line</span>
      </div>
    </form>
  );
}
